import { FC, ReactNode } from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { slugs } from '@/constants/slugs'
import { useTranslate } from '@/utils/translate/translate-client'
import {
  GButtonMD,
  GHeadingLG,
  GHeadingSM,
  GResponsiveText,
  GStepValueLG,
  GStepValueXL,
  GTitleLG,
  GTitleSM,
  GTitleXL,
  GTitleXS,
} from '@/views/PreSales/PreSalesGiveawayView/components/UI/Typography'
import { useGiveawayContext } from '@/views/PreSales/PreSalesGiveawayView/context/GiveawayContext'
import { GiveawayHowToStep } from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawayConfigData'

interface Props {
  steps: GiveawayHowToStep[]
  className?: string
}

export const HowItWorks: FC<Props> = ({ steps, className }) => {
  const { t } = useTranslate('tickets')
  const { scrollToProducts, theatricalSlug } = useGiveawayContext()

  const isNewDesign = [slugs.truthAndTreason, slugs.david, slugs.iWasAStranger].includes(theatricalSlug)

  return (
    <FlexColumn
      className={classNames('mx-auto max-w-[1080px] gap-4 pb-12 pt-[72px] md:pb-16 md:pt-[120px]', className)}
    >
      <GResponsiveText
        color="giveaway-text-primary-dark"
        className={'!font-bold'}
        MobileTextComponent={isNewDesign ? GHeadingSM : GTitleLG}
        DesktopTextComponent={isNewDesign ? GHeadingLG : GTitleXL}
      >
        {t('howItWorks', 'How it Works')}
      </GResponsiveText>

      {steps.map((step, index) => (
        <StepValue key={step.title} isNewDesign={isNewDesign} step={(index + 1).toString().padStart(2, '0')}>
          <strong>{step.title}</strong>: {step.description}
        </StepValue>
      ))}
      <Button
        variant="core-gray-400"
        className={classNames(
          "mt-6 hidden !border-2 !border-giveaway-text-subtle-light px-[24px] pb-[15px] pt-[14px] !font-['bio-sans'] !text-giveaway-text-subtle-light md:block md:w-fit",
          theatricalSlug === slugs.sketch ? 'rounded-none' : 'rounded-xl',
        )}
        outline
        onClick={scrollToProducts}
      >
        <GButtonMD>{t('preOrderTickets', 'Pre-Order Tickets')}</GButtonMD>
      </Button>
    </FlexColumn>
  )
}

const StepValue = ({ step, children, isNewDesign }: { step: string; children: ReactNode; isNewDesign: boolean }) => (
  <div className="mb-4 flex items-start justify-start gap-2">
    <div
      className={classNames(
        'flex rounded-md   px-2 py-0.5',
        isNewDesign ? 'bg-giveaway-primary-accent' : 'bg-giveaway-secondary',
        slugs.iWasAStranger && '!bg-giveaway-primary-subtle-dark',
      )}
    >
      <GResponsiveText
        color="giveaway-text-primary-light"
        MobileTextComponent={GStepValueLG}
        DesktopTextComponent={GStepValueXL}
      >
        {step}
      </GResponsiveText>
    </div>
    <GResponsiveText
      color="giveaway-text-primary-dark"
      className="!font-normal"
      MobileTextComponent={GTitleXS}
      DesktopTextComponent={GTitleSM}
    >
      {children}
    </GResponsiveText>
  </div>
)
